import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Text, Flex } from '@atlaskit/primitives/compiled';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Button from '@atlaskit/button/new';

import { fg } from '@confluence/feature-gating';

import { useAudioBriefing } from '../../hooks/useAudioBriefing';

const i18n = defineMessages({
	playbackSpeedLabel: {
		id: 'audio.miniplayer-playback-speed-control.playback-speed-label',
		defaultMessage: 'Playback Speed',
		description:
			'The label is used as a tooltip and for accessibility for the select dropdown which provides playback speed options for the actively playing audio',
	},
	speedLabel08x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-08x',
		defaultMessage: '0.8x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 0.8x',
	},
	speedLabel10x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-10x',
		defaultMessage: '1x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.0x',
	},
	speedLabel12x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-12x',
		defaultMessage: '1.2x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.2x',
	},
	speedLabel15x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-15x',
		defaultMessage: '1.5x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.5x',
	},
	speedLabel17x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-17x',
		defaultMessage: '1.7x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 1.7x',
	},
	speedLabel20x: {
		id: 'audio.miniplayer-playback-speed-control.speed-label-20x',
		defaultMessage: '2x',
		description:
			'The label is is shown on the select option in the audio miniplayer controls for audio playback speed of 2.0x',
	},
});

type PlaybackSpeedControlProps = {
	// Temporarily optional due to dependence of unused audio components on this component
	audioRef?: React.RefObject<HTMLAudioElement>;
	isDisabled?: boolean;
};

export const PlaybackSpeedControl = ({ audioRef, isDisabled }: PlaybackSpeedControlProps) => {
	const intl = useIntl();

	const [{ playbackSpeed }, { setContentPlaybackSpeed }] = useAudioBriefing();

	const handleSetSpeed = useCallback(
		(speed: number) => {
			setContentPlaybackSpeed(speed);

			const audio = audioRef?.current;
			if (audio) {
				audio.playbackRate = speed;
			}
		},
		[setContentPlaybackSpeed, audioRef],
	);

	const PLAYBACK_RATES = useMemo(
		() => [
			{ label: intl.formatMessage(i18n.speedLabel08x), value: 0.8 },
			{ label: intl.formatMessage(i18n.speedLabel10x), value: 1.0 },
			{ label: intl.formatMessage(i18n.speedLabel12x), value: 1.2 },
			{ label: intl.formatMessage(i18n.speedLabel15x), value: 1.5 },
			{ label: intl.formatMessage(i18n.speedLabel17x), value: 1.7 },
			{ label: intl.formatMessage(i18n.speedLabel20x), value: 2.0 },
		],
		[intl],
	);

	const getLabel = (speed: number) => PLAYBACK_RATES.find((item) => item.value === speed)?.label;

	return (
		<DropdownMenu
			placement="top-start"
			trigger={({ triggerRef, ...triggerProps }) => (
				<Flex justifyContent="center">
					<Button {...triggerProps} ref={triggerRef} appearance="subtle" isDisabled={isDisabled}>
						<Text color={isDisabled ? 'color.text.disabled' : 'color.text.subtle'} weight="medium">
							{getLabel(playbackSpeed)}
						</Text>
					</Button>
				</Flex>
			)}
			shouldRenderToParent={fg('should-render-to-parent-should-be-trueconfluence-a')}
		>
			<DropdownItemGroup>
				{PLAYBACK_RATES.map(({ label, value }) => (
					<DropdownItem key={value} onClick={() => handleSetSpeed(value)}>
						{label}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
